import { api } from './client'

/**
 * Transit stage of the parcel itself. Terminal outcomes (delivered / returned)
 * live on the sales order, not here — read `carrier_status_code` for what the
 * carrier last reported.
 */
export type CarrierShipmentStatus =
    'pending' | 'created' | 'picked_up' | 'in_transit' | 'cancelled'

export interface CarrierShipmentEvent {
    id: string
    carrier_shipment_id: string
    carrier_status_code: string
    status: CarrierShipmentStatus
    note: string
    event_at: string
    created_at: string
}

export interface CarrierShipment {
    id: string
    order_id: string
    carrier: string
    tracking_code?: string
    service_code?: string
    status: CarrierShipmentStatus
    carrier_status_code?: string
    cod_amount: number
    shipping_fee: number
    allow_inspection?: boolean
    inspection_fee_vnd: number
    label_url?: string
    idempotency_key: string
    return_decision?: 'approve_return' | 'redeliver'
    return_decision_state?:
        'pending' | 'carrier_confirmed' | 'confirmed' | 'reconcile_required'
    created_at: string
    updated_at: string
    /** The pre-merger commune the label prints, when one was chosen. */
    carrier_legacy_ward_id?: number | null
    legacy_ward_name?: string | null
    legacy_district_name?: string | null
    /** Oldest first; only the read endpoint returns them. */
    events?: CarrierShipmentEvent[]
}

export interface CarrierServiceOption {
    service_code: string
    service_name: string
    fee: number
    lead_time: string
}

/** The carrier's own administrative unit ids, picked by hand after a 422. */
export interface CarrierManualUnits {
    vtp_province_id: number
    vtp_ward_id: number
}

/** Receiver block as staff corrected it on the booking form. Absent = keep the order's value. */
export interface CarrierReceiverPatch {
    receiver_name?: string
    receiver_phone?: string
    receiver_address?: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
}

export interface CarrierQuoteRequest extends CarrierReceiverPatch {
    weight_gram: number
    manual_vtp_units?: CarrierManualUnits
    vtp_legacy_ward_id?: number
}

export type CarrierShippingFeePayer = 'company' | 'customer'

export interface CarrierDispatchRequest extends CarrierReceiverPatch {
    service_code: string
    weight_gram: number
    /** Goods amount to collect; omitted = the order's outstanding goods.
     *  The backend adds freight on top when the recipient pays it. */
    cod_amount?: number
    /** Parcels in the consignment; written to the order and sent to the carrier. */
    package_count?: number
    shipping_fee_payer: CarrierShippingFeePayer
    allow_inspection: boolean
    inspection_fee_vnd?: number
    note?: string
    manual_vtp_units?: CarrierManualUnits
    vtp_legacy_ward_id?: number
}

/** One pre-merger commune the carrier label can print for the order's ward. */
export interface CarrierLegacyUnitCandidate {
    vtp_ward_id: number
    ward_name: string
    district_name: string
    /** Other candidates only: the one current ward the commune now belongs
     *  to; absent when it matched none or was split between several. */
    current_ward_code?: string
    current_ward_name?: string
}

/** Where the pre-selected commune came from; null when nothing is suggested. */
export type CarrierLegacyUnitSource =
    'single' | 'previous_order' | 'address' | 'default'

export interface CarrierLegacyUnitSuggestion {
    candidates: CarrierLegacyUnitCandidate[]
    other_candidates: CarrierLegacyUnitCandidate[]
    suggested_vtp_ward_id: number | null
    source: CarrierLegacyUnitSource | null
    /** The old commune the address names when it now belongs to another
     *  current ward: the order's ward was likely entered wrong. Absent from a
     *  server older than this field. */
    address_ward?: CarrierLegacyUnitCandidate | null
}

/** Receiver edits still being typed; the suggestion follows them. */
export interface CarrierLegacyUnitParams {
    ward_code?: string
    receiver_address?: string
}

/**
 * An old commune found by name anywhere in the province, with the current ward
 * it now belongs to; picking one of another ward moves the receiver there.
 */
export interface CarrierLegacyUnitHit {
    ward_code: string
    ward_name: string
    vtp_ward_id: number
    legacy_ward_name: string
    legacy_district_name: string
}

export interface CarrierLegacyUnitSearchParams {
    q: string
    /** The receiver's province as typed; defaults to the order's. */
    province_code?: string
}

export interface CarrierCancelRequest {
    note?: string
}

export interface CarrierReturnDecisionRequest {
    decision: 'approve_return' | 'request_redelivery'
    note?: string
}

/** `details` of the 422 that asks for a manual carrier-unit pick. */
export interface CarrierAddressNeedsManualDetails {
    ward_code?: string
    province_code?: string
    /** The carrier's province when a mapped sibling ward pins it down; 0 when unknown. */
    vtp_province_id?: number
}

export const CARRIER_ADDRESS_NEEDS_MANUAL = 'ADDRESS_NEEDS_MANUAL_UNIT'
export const CARRIER_SHIPMENT_STATE_UNKNOWN = 'CARRIER_SHIPMENT_STATE_UNKNOWN'
export const CARRIER_SHIPMENT_NOT_FOUND = 'CARRIER_SHIPMENT_NOT_FOUND'
export const CARRIER_LEGACY_UNIT_REQUIRED = 'LEGACY_UNIT_REQUIRED'
export const CARRIER_LEGACY_UNIT_NOT_CANDIDATE = 'LEGACY_UNIT_NOT_CANDIDATE'

export const carrierShipmentApi = {
    get: (orderId: string) =>
        // An internally delivered order has no shipment; the 404 is data.
        api.get<CarrierShipment>(`/sales-orders/${orderId}/carrier-shipment`, {
            expected: [{ status: 404, code: CARRIER_SHIPMENT_NOT_FOUND }],
        }),
    legacyUnits: (orderId: string, params: CarrierLegacyUnitParams) =>
        api.get<CarrierLegacyUnitSuggestion>(
            `/sales-orders/${orderId}/carrier-shipment/legacy-units`,
            { params },
        ),
    searchLegacyUnits: (
        orderId: string,
        params: CarrierLegacyUnitSearchParams,
    ) =>
        api.get<CarrierLegacyUnitHit[]>(
            `/sales-orders/${orderId}/carrier-shipment/legacy-units/search`,
            { params },
        ),
    quote: (orderId: string, body: CarrierQuoteRequest) =>
        api.post<CarrierServiceOption[]>(
            `/sales-orders/${orderId}/carrier-shipment/quote`,
            body,
        ),
    dispatch: (orderId: string, body: CarrierDispatchRequest) =>
        api.post<CarrierShipment>(
            `/sales-orders/${orderId}/carrier-shipment`,
            body,
        ),
    cancel: (orderId: string, body: CarrierCancelRequest) =>
        api.post<{ status: string }>(
            `/sales-orders/${orderId}/carrier-shipment/cancel`,
            body,
        ),
    resolveReturn: (orderId: string, body: CarrierReturnDecisionRequest) =>
        api.post<{ status: string }>(
            `/sales-orders/${orderId}/carrier-shipment/return-decision`,
            body,
        ),
    // The print code carries its own expiry, so the link is fetched per click
    // rather than stored on the shipment.
    label: (orderId: string) =>
        api.get<{ label_url: string }>(
            `/sales-orders/${orderId}/carrier-shipment/label`,
        ),
}
